import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import { type DropResult } from '@hello-pangea/dnd';
import { useQueryClient } from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';
import { exception } from '@erikdakoda/log/exception';
import { useSnack } from '@erikdakoda/ui/hooks/useSnack';
import { restoreItems } from '@erikdakoda/ui/shared/restoreItems';
import { useUpdateTodoList } from '@erikdakoda/database/hooks/todo-list';
import LabelSidebar from '@erikdakoda/label-ui/components/LabelSidebar';

import type { TodoItemForSidebar, TodoListForSidebar } from '@erikdakoda/todo/types';
import type {
  LabelDragEndHandler,
  LabelDragEndHandlerSetter,
  LabelItemRenderer,
  LabelMoveItemArgs,
  LabelSidebarCallbacks,
  LabelSidebarSection,
} from '@erikdakoda/label-ui/types';
import { parseSectionDraggableId } from '@erikdakoda/label-ui/shared/labelDragIds';
import { routeLabelDragEnd } from '@erikdakoda/label-ui/shared/routeLabelDragEnd';
import {
  buildTodoSections,
  toTodoListOrder,
  type TodoGroupListEntry,
} from '../shared/todoLabelSections';
import TodoListItem from './TodoListItem';

/** Dnd type of todo item drags; shared by every group so items can move between groups. */
const TODO_ITEM_DND_TYPE = 'todoItem';

const noMutation = async (): Promise<void> => undefined;

type GroupedSection = {
  group: string;
  lists: TodoGroupListEntry[];
};

type TodoSidebarListProps = {
  groupedSections: GroupedSection[];
  filterActive: boolean;
  selectedTodoId: string | null;
  onSelect: (id: string) => void;
  onStateChange: (id: string, state: TodoItemForSidebar['state']) => void;
  onTrashed?: (listId: string) => void;
  onItemTrashed?: (todoId: string) => void;
  refetch?: () => Promise<void>;
  // A TanStack Query key, inherently `readonly unknown[]`. The ZenStack 3 hooks return exactly that
  // type and it is only forwarded opaquely to `setQueriesData` / `invalidateQueries`, so the
  // previous fixed 5-tuple over-specified it and rejected the hook value.
  queryKey: readonly unknown[];
  setDragEndHandler?: LabelDragEndHandlerSetter;
  todoLists: TodoListForSidebar[];
  /** Owner of the todo rows; carried on the shared sidebar's item shape. */
  ownerId: string;
  spaceId: string | null;
};

/**
 * Grouped todo sidebar rendered through the shared `LabelSidebar` — one instance per todo group,
 * whose sections are that group's `TodoList`s. Drags keep posting to the todo move endpoints; the
 * todo data model is untouched.
 */
export default function TodoSidebarList({
  groupedSections,
  filterActive,
  selectedTodoId,
  onSelect,
  onStateChange,
  onTrashed,
  onItemTrashed,
  refetch,
  queryKey,
  setDragEndHandler,
  todoLists,
  ownerId,
  spaceId,
}: TodoSidebarListProps) {
  'use memo';

  const { t } = useTranslation('todo-ui');
  const queryClient = useQueryClient();
  const { snack } = useSnack();
  const updateTodoList = useUpdateTodoList().mutateAsync;

  const todoItemsById = new Map<string, TodoItemForSidebar>();
  for (const list of todoLists) {
    for (const item of list.todoItems) {
      todoItemsById.set(item.id, item);
    }
  }

  const itemLookupRef = useRef(todoItemsById);
  itemLookupRef.current = todoItemsById;

  const itemCallbacks = useRef({ onSelect, onStateChange, onItemTrashed, selectedTodoId });
  itemCallbacks.current = { onSelect, onStateChange, onItemTrashed, selectedTodoId };

  // keep: identity is read by every LabelSidebar instance on every render
  const renderItem: LabelItemRenderer = useCallback(({ item }) => {
    const todoItem = itemLookupRef.current.get(item.id);
    if (!todoItem) return null;
    const {
      onSelect: select,
      onStateChange: changeState,
      onItemTrashed: trashed,
      selectedTodoId: selectedId,
    } = itemCallbacks.current;
    return (
      <TodoListItem
        item={todoItem}
        selected={selectedId === todoItem.id}
        onSelect={select}
        onStateChange={changeState}
        onTrashed={trashed}
      />
    );
  }, []);

  const sectionsByGroup = groupedSections.map(({ group, lists }) => ({
    group,
    sections: buildTodoSections({ lists, ownerId, spaceId }),
  }));

  const moveState = useRef({ todoLists, refetch, onTrashed, sectionsByGroup });
  moveState.current = { todoLists, refetch, onTrashed, sectionsByGroup };

  const moveTodoListOrder = async (group: string, sectionIds: readonly string[]): Promise<void> => {
    const groupListIds = moveState.current.todoLists
      .filter((list) => list.group === group)
      .map((list) => list.id);
    const todoListIds = toTodoListOrder(sectionIds, groupListIds);
    if (todoListIds.length !== groupListIds.length) return;

    queryClient.setQueriesData({ queryKey }, (data: unknown) => {
      if (!Array.isArray(data)) return data;
      const updatedData = cloneDeep(data) as TodoListForSidebar[];
      const ordById = new Map(todoListIds.map((id, ord) => [id, ord]));
      for (const list of updatedData) {
        const nextOrd = ordById.get(list.id);
        if (nextOrd !== undefined) {
          list.ord = nextOrd;
        }
      }
      return updatedData.sort((left, right) => {
        const groupCompare = left.group.localeCompare(right.group);
        if (groupCompare !== 0) return groupCompare;
        return left.ord - right.ord;
      });
    });

    try {
      const response = await fetch('/api/move-todo-list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ todoListIds }),
      });
      const { message, error } = (await response.json()) as { message: string; error?: string };
      if (error) throw new Error(error);
      snack.success(message);
      await moveState.current.refetch?.();
    } catch (err: unknown) {
      exception(err);
    } finally {
      queryClient.invalidateQueries({ queryKey });
    }
  };

  const moveTodoItem = async ({
    item,
    sourceSectionId,
    sourceOrd,
    destSectionId,
    destOrd,
  }: LabelMoveItemArgs): Promise<void> => {
    queryClient.setQueriesData({ queryKey }, (data: unknown) => {
      if (!Array.isArray(data)) return data;
      const updatedData = cloneDeep(data) as TodoListForSidebar[];
      const sourceList = updatedData.find((list) => list.id === sourceSectionId);
      const destList = updatedData.find((list) => list.id === destSectionId);
      if (!sourceList || !destList) return updatedData;
      const itemIndex = sourceList.todoItems.findIndex((row) => row.id === item.id);
      if (itemIndex === -1) return updatedData;
      const [draggedItem] = sourceList.todoItems.splice(itemIndex, 1);
      if (!draggedItem) return updatedData;
      draggedItem.todoListId = destSectionId;
      destList.todoItems.splice(destOrd, 0, draggedItem);
      return updatedData;
    });

    try {
      const response = await fetch('/api/move-todo-item', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemId: item.id,
          sourceListId: sourceSectionId,
          sourceOrd,
          destListId: destSectionId,
          destOrd,
        }),
      });
      const { message, error } = (await response.json()) as { message: string; error?: string };
      if (error) throw new Error(error);
      snack.success(message);
      await moveState.current.refetch?.();
    } catch (err: unknown) {
      exception(err);
    } finally {
      queryClient.invalidateQueries({ queryKey });
    }
  };

  const mutationsRef = useRef({ moveTodoListOrder, moveTodoItem });
  mutationsRef.current = { moveTodoListOrder, moveTodoItem };

  // A todo item belongs to exactly one list, so `copyDisabled` keeps every drop a move and the
  // remove zone is hidden — `onCopyItem` / `onRemoveItem` can never fire.
  const makeCallbacks = (group: string): LabelSidebarCallbacks => ({
    onMoveSection: async ({ sectionIds }) => moveTodoListOrder(group, sectionIds),
    onMoveItem: moveTodoItem,
    onCopyItem: noMutation,
    onRemoveItem: noMutation,
  });

  const handlersRef = useRef(new Map<string, LabelDragEndHandler>());
  const settersRef = useRef(new Map<string, LabelDragEndHandlerSetter>());

  function getDragEndHandlerSetter(group: string): LabelDragEndHandlerSetter {
    const existing = settersRef.current.get(group);
    if (existing) return existing;
    // `LabelSidebar` registers React-setState style — `setDragEndHandler(() => handler)` — so a
    // function value is always the updater, never the handler itself.
    const setter: LabelDragEndHandlerSetter = (value) => {
      const next =
        typeof value === 'function'
          ? (
              value as (
                previous: LabelDragEndHandler | undefined,
              ) => LabelDragEndHandler | undefined
            )(handlersRef.current.get(group))
          : value;
      if (next) {
        handlersRef.current.set(group, next);
      } else {
        handlersRef.current.delete(group);
      }
    };
    settersRef.current.set(group, setter);
    return setter;
  }

  // keep: stable identity for the setDragEndHandler registration below
  const handleDragEnd = useCallback(async (result: DropResult): Promise<boolean> => {
    // List reorder is scoped to one group, so let that group's sidebar claim it.
    if (parseSectionDraggableId(result.draggableId)) {
      for (const handler of handlersRef.current.values()) {
        if (await handler(result)) {
          return true;
        }
      }
      return false;
    }

    // Items may move between groups, so route them against every group's sections at once.
    const allSections: LabelSidebarSection[] = moveState.current.sectionsByGroup.flatMap(
      (entry) => entry.sections,
    );
    return routeLabelDragEnd(result, {
      droppableType: TODO_ITEM_DND_TYPE,
      sections: allSections,
      visibleSections: allSections,
      copyModifierActive: false,
      copyDisabled: true,
      onMoveSection: noMutation,
      onMoveItem: mutationsRef.current.moveTodoItem,
      onCopyItem: noMutation,
      onRemoveItem: noMutation,
    });
  }, []);

  useEffect(() => {
    setDragEndHandler?.(() => handleDragEnd);
  }, [handleDragEnd, setDragEndHandler]);

  const handleRenameSection = async (sectionId: string, name: string): Promise<void> => {
    const previousName = moveState.current.todoLists.find((list) => list.id === sectionId)?.name;
    await updateTodoList({ where: { id: sectionId }, data: { name } });
    const undo =
      previousName === undefined
        ? undefined
        : async (): Promise<void> => {
            await updateTodoList({ where: { id: sectionId }, data: { name: previousName } });
            await moveState.current.refetch?.();
          };
    snack.success(t('todoListRenamed'), undo ? { undo } : undefined);
    await moveState.current.refetch?.();
  };

  const handleTrashSection = async (sectionId: string): Promise<void> => {
    await updateTodoList({ where: { id: sectionId }, data: { trashedAt: new Date() } });
    const undo = async (): Promise<void> => {
      const { refusedIds = [] } = await restoreItems('TodoList', [sectionId]);
      await moveState.current.refetch?.();
      if (refusedIds.length > 0) {
        throw new Error(t('todoListRestoreFailed'));
      }
    };
    snack.success(t('todoListDeleted'), { undo });
    moveState.current.onTrashed?.(sectionId);
  };

  return (
    <Box className="todo-sidebar-list" sx={{ mb: '52px' }}>
      {sectionsByGroup.map(({ group, sections }) => (
        <LabelSidebar
          key={group}
          listId={`todos-${group}`}
          droppableType={TODO_ITEM_DND_TYPE}
          dragScope={`${TODO_ITEM_DND_TYPE}-${group}`}
          sections={sections}
          filter=""
          renderItem={renderItem}
          emptyText={filterActive ? t('noMatchingItems') : t('empty')}
          disableRemoveZone
          copyDisabled
          onRenameSection={async ({ section, name }) => handleRenameSection(section.id, name)}
          onTrashSection={async ({ section }) => handleTrashSection(section.id)}
          setDragEndHandler={getDragEndHandlerSetter(group)}
          refetch={refetch}
          {...makeCallbacks(group)}
        />
      ))}
    </Box>
  );
}
